/* eslint-disable no-useless-escape */
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import * as speechsdk from "microsoft-cognitiveservices-speech-sdk";
import { setIsMicOn, setMode, setTranscript } from "../../lib/redux/actions";

export default function useVoiceActions({
  isClosedRef,
  handleChatResponse,
  voiceOpenRef,
  synthesizer,
  audioStop,
  player,
  recognizer,
  isMicOnRef,
}) {
  const dispatch = useDispatch();
  const mode = useSelector((state) => state.voiceMode.mode);
  const transcript = useSelector((state) => state.voiceMode.transcript);

  const isInterrupted = React.useRef(false);
  const [speechConfig] = React.useState(() => {
    const config = speechsdk.SpeechConfig.fromSubscription(
      process.env.REACT_APP_AZURE_SPEECH_KEY,
      process.env.REACT_APP_AZURE_SPEECH_REGION
    );
    config.speechRecognitionLanguage = "en-IN";
    config.speechSynthesisVoiceName = "en-IN-NeerjaNeural";

    return config;
  });

  const startListening = () => {
    dispatch(setTranscript(""));
    if (!player.current) dispatch(setMode("listening"));

    if (recognizer.current) {
      recognizer.current.stopContinuousRecognitionAsync();
      recognizer.current.close();
      recognizer.current = null;
    }

    recognizer.current = new speechsdk.SpeechRecognizer(speechConfig);
    let responded = false;

    recognizer.current.recognized = (_, e) => {
      const finalTranscript = e.result.text.trim();
      if (
        e.result.reason === speechsdk.ResultReason.RecognizedSpeech &&
        finalTranscript &&
        !responded
      ) {
        responded = true;
        stopSpeaking();
        dispatch(setTranscript(finalTranscript));
        dispatch(setMode("thinking"));
        recognizer.current.stopContinuousRecognitionAsync();
        getHFReply(finalTranscript);
      }
    };

    recognizer.current.canceled = (_, e) => {
      console.error("Recognition canceled:", e.errorDetails);
      restartListeningSequence();
    };

    recognizer.current.sessionStopped = () => {
      recognizer.current.close();
      recognizer.current = null;
    };

    recognizer.current.startContinuousRecognitionAsync(
      () => dispatch(setIsMicOn(true)),
      (err) => {
        console.error("Failed to start recognition:", err);
        dispatch(setIsMicOn(false));
        throw err; // Rethrow to ensure the error is not silently swallowed
      }
    );
  };

  const stopListening = () => {
    if (recognizer.current) {
      recognizer.current.stopContinuousRecognitionAsync(
        () => {
          recognizer.current.close();
          recognizer.current = null;
          dispatch(setIsMicOn(false));
        },
        (err) => {
          console.error("Error stopping recognition:", err);
          recognizer.current = null;
          dispatch(setIsMicOn(false));
          throw err; // Rethrow to ensure the error is not silently swallowed
        }
      );
    }
  };

  const stopSpeaking = () => {
    if (synthesizer.current) {
      synthesizer.current.close();
      synthesizer.current = null;
    }
    if (player.current) {
      player.current.pause();
      player.current = null;
    }
  };

  const speakReply = async (text) => {
    const filtered = cleanText(text);
    isInterrupted.current = false;

    if (synthesizer.current) {
      synthesizer.current.close();
      synthesizer.current = null;
    }

    player.current = new speechsdk.SpeakerAudioDestination();
    const audioConfig = speechsdk.AudioConfig.fromSpeakerOutput(player.current);
    synthesizer.current = new speechsdk.SpeechSynthesizer(
      speechConfig,
      audioConfig
    );

    player.current.onAudioStart = () => {
      dispatch(setMode("speaking"));
      if (isMicOnRef.current) {
        startListeningInBackgroundWhileSpeaking();
      }
    };

    player.current.onAudioEnd = () => {
      if (!isInterrupted.current) {
        dispatch(setMode("listening"));
      }
      player.current = null;
      audioStop.current = null;
    };

    synthesizer.current.speakTextAsync(
      filtered,
      (result) => {
        if (
          result.reason === speechsdk.ResultReason.SynthesizingAudioCompleted
        ) {
          audioStop.current = true;
        } else {
          console.error("Speech synthesis failed:", result.errorDetails);
          if (isMicOnRef.current) {
            restartListeningSequence();
          }
        }
        synthesizer.current.close();
        synthesizer.current = null;
      },
      (error) => {
        console.error("TTS error:", error);
        synthesizer.current?.close();
        synthesizer.current = null;
        restartListeningSequence();
        throw error; // Rethrow to ensure the error is not silently swallowed
      }
    );
  };

  const startListeningInBackgroundWhileSpeaking = () => {
    if (recognizer.current) {
      recognizer.current.stopContinuousRecognitionAsync();
      recognizer.current.close();
      recognizer.current = null;
    }

    recognizer.current = new speechsdk.SpeechRecognizer(speechConfig);

    recognizer.current.recognized = (_, e) => {
      const spoken = e.result.text.trim();
      if (
        e.result.reason === speechsdk.ResultReason.RecognizedSpeech &&
        spoken
      ) {
        isInterrupted.current = true;
        stopSpeaking();
        dispatch(setTranscript(spoken));
        dispatch(setMode("thinking"));
        recognizer.current.stopContinuousRecognitionAsync();
        getHFReply(spoken);
      }
    };

    recognizer.current.canceled = (_, e) => {
      console.warn("Background recognition canceled:", e.errorDetails);
    };

    recognizer.current.sessionStopped = () => {
      recognizer.current.close();
      recognizer.current = null;
    };

    recognizer.current.startContinuousRecognitionAsync(
      () => console.warn("🎤 Background recognition started"),
      (err) => {
        console.error("❌ Background recognition failed:", err);
        throw err; // Rethrow to ensure the error is not silently swallowed
      }
    );
  };

  const stopBackgroundListening = () => {
    if (recognizer.current) {
      recognizer.current.stopContinuousRecognitionAsync(
        () => {
          recognizer.current.close();
          recognizer.current = null;
        },
        (err) => {
          console.error("Error stopping background recognition:", err);
          // Still attempt cleanup
          recognizer.current.close();
          recognizer.current = null;
          throw err; // Rethrow to ensure the error is not silently swallowed
        }
      );
    }
  };

  // Add a non-regex HTML tag stripper
  function stripTags(str) {
    let result = "";
    let inside = false;
    for (let c of str) {
      if (c === "<") inside = true;
      else if (c === ">") inside = false;
      else if (!inside) result += c;
    }
    return result;
  }

  // Add a non-regex Markdown link stripper
  function stripMarkdownLinks(str) {
    let result = "";
    let i = 0;
    while (i < str.length) {
      if (str[i] === "[") {
        let closeBracket = str.indexOf("]", i);
        let openParen = str.indexOf("(", closeBracket);
        let closeParen = str.indexOf(")", openParen);
        if (
          closeBracket !== -1 &&
          openParen === closeBracket + 1 &&
          closeParen !== -1
        ) {
          // Extract the link text
          result += str.slice(i + 1, closeBracket);
          i = closeParen + 1;
          continue;
        }
      }
      result += str[i];
      i++;
    }
    return result;
  }

  // eslint-disable-next-line
  const cleanText = (text) =>
    stripMarkdownLinks(
      stripTags(
        text
          .replace(/^#{1,6}\s*/gm, "")
          .replace(/(\*\*|__)(.*?)\1/g, "$2")
          .replace(/([*_])(.*?)\1/g, "$2")
          // .replace(/\[([^\]]*)\]\(([^)\s]*)\)/g, "$1") // removed regex-based Markdown link stripper
          // .replace(/<[^>]*>/g, "") // removed regex-based tag stripper
          .replace(/https?:\/\/\S+/g, "")
          .replace(/```[\s\S]*?```/g, "")
          .replace(/`[^`]+`/g, "")
          .replace(/[(){}\[\]/\\]/g, "")
          .replace(/!\[([^\]]+)\]\([^)]+\)/g, "$1")
          .replace(/^>\s*/gm, "")
          .replace(/^[-*]{3,}$/gm, "")
          .split("\n")
          .map((line) => line.trim())
          .filter((line) => line.length > 0)
          .join(" ")
          .replace(/\s+/g, " ")
          .trim()
      )
    );

  const getHFReply = async (prompt) => {
    dispatch(setMode("thinking"));
    handleChatResponse({ text: prompt, value: null });
  };

  const restartListeningSequence = () => {
    if (isClosedRef.current) return;
    if (!player.current) dispatch(setMode("idle"));

    if (isMicOnRef.current) {
      setTimeout(() => {
        if (!isClosedRef.current) startListening();
      }, 500);
    } else {
      dispatch(setMode("listening"));
    }
  };

  return {
    mode,
    transcript,
    setMode: (m) => dispatch(setMode(m)),
    startListening,
    stopListening,
    speakReply,
    stopSpeaking,
    startListeningInBackgroundWhileSpeaking,
    stopBackgroundListening,
  };
}
